import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { IoEyeOutline, IoEyeOffOutline } from 'react-icons/io5';
import loginWms from '../assets/loginWms.png';
import jsLogo from '../assets/JDLogo.png';
import ResetPasswordModal from '../components/common/ResetPasswordModal';
import { loginService } from '../services/api';

const Login = ({ onLogin }) => {
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const navigate = useNavigate();

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail || !forgotEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    setForgotLoading(true);
    try {
      toast.success('If this email is registered, you will receive a confirmation code.');
      setResetPasswordOpen(false);
      setForgotEmail('');
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!code || !password) {
      toast.error('Please enter both code and password');
      return;
    }

    setIsLoading(true);
    try {
      const response = await loginService.login({
        code: code.trim(),
        password: password,
      });

      if (response.success) {
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userCode', response.warehouse.code);
        localStorage.setItem('warehouseId', response.warehouse.id);
        localStorage.setItem('warehouseName', response.warehouse.name);

        toast.success('Login successful!');
        onLogin();
        navigate('/');
      } else {
        toast.error(response.message || 'Login failed');
      }
    } catch (error) {
      toast.error(error.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel - 55%, 100vh, image + overlay */}
      <div
        className="hidden lg:block lg:w-[55%] relative overflow-hidden bg-gray-200"
        style={{ height: '100vh' }}
      >
        <img
          src={loginWms}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute z-10"
          style={{ top: 56, left: 56, maxWidth: 520 }}
        >
          <h1
            style={{
              fontSize: 44,
              fontWeight: 700,
              lineHeight: 1.1,
            }}
          >
            <span style={{ color: '#1E2B57' }}>Logistics for </span>
            <span style={{ color: '#188742' }}>Perishables</span>
          </h1>
          <p
            style={{
              marginTop: 14,
              fontSize: 16,
              lineHeight: 1.6,
              color: '#3A3A3A',
            }}
          >
            Empowering your supply chain with real-time analytics and operations
            management.
          </p>
        </div>
        <div
          className="absolute z-10"
          style={{ left: 56, bottom: 40, color: '#2F2F2F', fontSize: 14 }}
        >
          © 2025 JustDelivries. All rights reserved.
        </div>
      </div>

      {/* Right panel - 45%, centered auth card */}
      <div
        className="flex-1 flex items-center justify-center p-6 lg:p-8 bg-white min-h-screen lg:w-[45%]"
        style={{ minHeight: '100vh' }}
      >
        <div className="w-full max-w-[460px]">
          {!resetPasswordOpen ? (
            <>
              <div className="flex flex-col items-center mb-6">
                <img
                  src={jsLogo}
                  alt=""
                  className="h-auto max-h-16 w-auto"
                />
              </div>
              <div
                className="w-full mb-6"
                style={{ height: 1, backgroundColor: '#E6E6E6' }}
              />
              <h1
                className="text-center"
                style={{
                  fontSize: 26,
                  fontWeight: 600,
                  color: '#2B2B2B',
                }}
              >
                Sign in
              </h1>
              <p
                className="text-center mb-6"
                style={{
                  fontSize: 14,
                  color: '#6B6B6B',
                  marginTop: 6,
                }}
              >
                To Access WMS Portal
              </p>

              {/* Login Form */}
              <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label
                htmlFor="code"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {/* Email Address
                 */}
                 Code
              </label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter your code"
                disabled={isLoading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white focus:outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600 transition-colors disabled:opacity-60"
              />
            </div>

            <div className="mb-5">
              <div className="flex items-center justify-between mb-1">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => setResetPasswordOpen(true)}
                  className="text-sm font-medium hover:underline"
                  style={{ color: '#188742' }}
                >
                  Forgot Password ?
                </button>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  className="w-full px-4 py-3 pr-11 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white focus:outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600 transition-colors disabled:opacity-60"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <IoEyeOffOutline className="w-5 h-5" />
                  ) : (
                    <IoEyeOutline className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* 4) Login button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 rounded-lg text-white font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed bg-[#188742] hover:bg-[#15733a] disabled:bg-[#9ca3af] disabled:hover:bg-[#9ca3af]"
            >
              {isLoading ? 'Signing in...' : 'Login'}
            </button>
          </form>

          {/* 5) Footer text */}
              <p className="mt-6 text-center text-xs text-gray-500">
                By Logging in you agree to our{' '}
                <span className="font-medium" style={{ color: '#188742' }}>
                  Terms & Conditions
                </span>
                .
              </p>
            </>
          ) : (
            <>
              <div className="flex flex-col items-center mb-6">
                <img
                  src={jsLogo}
                  alt=""
                  className="h-auto max-h-16 w-auto"
                />
                <p className="text-sm text-gray-500 mt-2">Logistics for Perishables</p>
              </div>
              <div
                className="w-full mb-6"
                style={{ height: 1, backgroundColor: '#E6E6E6' }}
              />
              <h1
                className="text-center"
                style={{
                  fontSize: 26,
                  fontWeight: 600,
                  color: '#2B2B2B',
                }}
              >
                Forgot Password?
              </h1>
              <p
                className="text-center mb-6"
                style={{
                  fontSize: 14,
                  color: '#6B6B6B',
                  marginTop: 6,
                }}
              >
                Please enter your email address & we will send a confirmation code.
              </p>

              <form onSubmit={handleForgotSubmit}>
                <div className="mb-5">
                  <label
                    htmlFor="forgot-email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Email Address
                  </label>
                  <input
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="Enter your email address"
                    disabled={forgotLoading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white focus:outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600 transition-colors disabled:opacity-60"
                  />
                </div>
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full h-12 rounded-lg text-white font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed bg-[#188742] hover:bg-[#15733a] disabled:bg-[#9ca3af] disabled:hover:bg-[#9ca3af]"
                >
                  {forgotLoading ? 'Sending...' : 'Send OTP'}
                </button>
              </form>

              <p className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => setResetPasswordOpen(false)}
                  className="text-sm font-medium hover:underline"
                  style={{ color: '#188742' }}
                >
                  Back to Sign in
                </button>
              </p>
            </>
          )}
        </div>
      </div>

      <ResetPasswordModal
        isOpen={false}
        onClose={() => setResetPasswordOpen(false)}
      />
    </div>
  );
};

export default Login;
